/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { jsx } from '@emotion/react';

import * as React from 'react';

import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { Button, Grid, Typography, Typography2 } from '@coursera/cds-core';
import { TrashIcon } from '@coursera/cds-icons';

import type { FilterOption } from 'bundles/enterprise-admin-drawers/components/types';
import ActivityFilterSectionZeroState from 'bundles/enterprise-admin-user-filters/components/ActivityFilterSectionZeroState';
import type {
  UserFilterBuilderError,
  UserFilterBuilderErrors,
} from 'bundles/enterprise-admin-user-filters/components/ErrorsNotification';
import CompletedActivityFilter from 'bundles/enterprise-admin-user-filters/components/activity-filters/CompletedActivityFilter';
import EnrolledButInactiveActivityFilter from 'bundles/enterprise-admin-user-filters/components/activity-filters/EnrolledButInactiveActivityFilter';
import EnrolledInACourseActivityFilter from 'bundles/enterprise-admin-user-filters/components/activity-filters/EnrolledInACourseActivityFilter';
import GeneralInactivityFilter from 'bundles/enterprise-admin-user-filters/components/activity-filters/GeneralInactivityFilter';
import SpecializationInProgressActivityFilter from 'bundles/enterprise-admin-user-filters/components/activity-filters/SpecializationInProgressActivityFilter';
import { Section, SubSection } from 'bundles/enterprise-admin-user-filters/components/ui';
import { getActivityFilterLabel } from 'bundles/enterprise-admin-user-filters/constants';
import type { ActivityFilter, EnrichedProduct } from 'bundles/enterprise-admin-user-filters/types';
import {
  convertFilterOptionsToEnrichedProducts,
  convertFilterOptionsToProducts,
} from 'bundles/enterprise-admin-user-filters/utils';
import { ConfirmationDialog, Spacer, useModalState } from 'bundles/enterprise-ui';

import _t from 'i18n!nls/enterprise-admin-user-filters';

type Props = {
  errors: UserFilterBuilderErrors;
  activityFilter?: ActivityFilter;
  selectedCourses: FilterOption[];
  onAddActivityFilter: () => void;
  onChangeActivityFilter: () => void;
  onRemoveActivityFilter: () => void;
  onActivityFilterChange: (activityFilter: Partial<ActivityFilter>) => void;
  onEnrichedProductsChange: (enrichedProducts: EnrichedProduct[]) => void;
  onClearError: (error: UserFilterBuilderError) => void;
};

const ActivityFilterSection = ({
  errors,
  activityFilter,
  selectedCourses,
  onAddActivityFilter,
  onChangeActivityFilter,
  onRemoveActivityFilter,
  onActivityFilterChange,
  onClearError,
  onEnrichedProductsChange,
}: Props) => {
  const [removalConfirmationOpen, showRemovalConfirmation, hideRemovalConfirmation] = useModalState();

  const onCoursesSelected = (products: FilterOption[]) => {
    onActivityFilterChange({ productIdSelection: { values: convertFilterOptionsToProducts(products) } });
    onEnrichedProductsChange(convertFilterOptionsToEnrichedProducts(products));
  };

  const renderAccordions = () => {
    let children: JSX.Element | null = null;

    if (activityFilter?.type === 'NO_ENROLLMENTS') {
      children = (
        <SubSection>
          <Typography variant="h2" css={{ margin: 16 }} data-testid="never-enrolled">
            {_t('Never enrolled in any courses')}
          </Typography>
        </SubSection>
      );
    } else if (activityFilter?.type === 'GENERAL_INACTIVITY') {
      children = (
        <SubSection>
          <GeneralInactivityFilter
            errors={errors}
            activityFilter={activityFilter}
            onActivityFilterChange={onActivityFilterChange}
            onClearError={onClearError}
          />
        </SubSection>
      );
    } else if (activityFilter?.type === 'ENROLLED') {
      return (
        <EnrolledInACourseActivityFilter
          selectedCourses={selectedCourses}
          activityFilter={activityFilter}
          onCoursesSelected={onCoursesSelected}
          onActivityFilterChange={onActivityFilterChange}
        />
      );
    } else if (activityFilter?.type === 'ENROLLED_BUT_INACTIVE') {
      return (
        <EnrolledButInactiveActivityFilter
          errors={errors}
          selectedCourses={selectedCourses}
          activityFilter={activityFilter}
          onCoursesSelected={onCoursesSelected}
          onActivityFilterChange={onActivityFilterChange}
          onClearError={onClearError}
        />
      );
    } else if (activityFilter?.type === 'COMPLETED') {
      children = (
        <CompletedActivityFilter
          errors={errors}
          selectedCourses={selectedCourses}
          activityFilter={activityFilter}
          onCoursesSelected={onCoursesSelected}
          onActivityFilterChange={onActivityFilterChange}
          onClearError={onClearError}
        />
      );
    } else if (activityFilter?.type === 'PROGRESS_IN_S12N') {
      children = (
        <SpecializationInProgressActivityFilter
          selectedCourses={selectedCourses}
          activityFilter={activityFilter}
          setSelectedCourses={onCoursesSelected}
          onActivityFilterChange={onActivityFilterChange}
        />
      );
    }
    return children;
  };

  if (activityFilter) {
    return (
      <Section>
        <Typography2 component="h3" variant="subtitleMedium">
          <FormattedMessage
            message={_t('Activity filter: {title}')}
            title={getActivityFilterLabel(activityFilter.type)}
          />
        </Typography2>
        <Spacer size={16} />
        {renderAccordions()}
        <Spacer size={12} />
        <Grid container alignItems="center" spacing={16}>
          <Grid item>
            <Typography2 component="p">{_t('Actions')}</Typography2>
          </Grid>
          <Grid item>
            <Button variant="ghost" size="small" onClick={onChangeActivityFilter} data-testid="change-activity-filter">
              {_t('Change activity filter')}
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="ghost"
              size="small"
              icon={<TrashIcon size="small" />}
              onClick={showRemovalConfirmation}
              data-testid="remove-activity-filter"
            >
              {_t('Remove activity filter')}
            </Button>
          </Grid>
        </Grid>
        <ConfirmationDialog
          open={removalConfirmationOpen}
          heading={_t('Confirm activity filter removal?')}
          confirmationLabel={_t('Remove filter')}
          onConfirm={onRemoveActivityFilter}
          onClose={hideRemovalConfirmation}
        >
          <Typography2 component="p">
            {_t('This will remove the current activity filters, you can always add activity filters again.')}
          </Typography2>
        </ConfirmationDialog>
      </Section>
    );
  }
  return <ActivityFilterSectionZeroState onAddActivityFilter={onAddActivityFilter} />;
};

export default ActivityFilterSection;
